import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePoll } from '../contexts/PollContext';
import { usePlayer } from '../contexts/PlayerContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { ArrowLeft, AlertCircle, TrendingUp } from 'lucide-react';

interface PollData {
  id: string;
  title: string;
  question: string;
  options: Array<{
    id: string;
    emoji: string;
    label?: string;
  }>;
  state: 'open' | 'active' | 'closed';
  stayActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

function PollLobby() {
  const { 
    connected, 
    players, 
    currentPoll,
    submitVote,
    joinPollRoom
  } = usePoll();
  
  const { player } = usePlayer();
  const { roomId: urlRoomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  
  const [pollData, setPollData] = useState<PollData | null>(null);
  const [isCheckingPollState, setIsCheckingPollState] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debug logging for PollLobby state
  console.log('🔍 PollLobby state:', {
    urlRoomId,
    connected,
    currentPoll: !!currentPoll,
    pollData: !!pollData,
    pollDataState: pollData?.state,
    pollDataQuestion: pollData?.question,
    isCheckingPollState,
    error,
    playersCount: players.length
  });

  // Check poll state when component mounts
  useEffect(() => {
    const checkPollState = async () => {
      if (!urlRoomId) return;
      
      try {
        setIsCheckingPollState(true);
        setError(null);
        
        // Room ID is the poll ID (already has poll_ prefix)
        const pollId = urlRoomId;
        const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
        const response = await fetch(`${serverUrl}/api/admin/polls/${pollId}`);
        
        if (response.ok) {
          const data = await response.json();
          const poll = data.poll;
          console.log('📊 PollLobby: Fetched poll data:', {
            pollId: poll.id,
            state: poll.state,
            question: poll.question,
            optionsCount: poll.options?.length
          });
          setPollData(poll);
          
          // If poll is closed, fetch results
          if (poll.state === 'closed') {
            console.log('🔒 Poll is closed, should show results');
            // For now, we'll show a closed message. In a real app, you'd fetch results from the server
            // This would require an API endpoint to get poll results
          }
        } else {
          console.warn('⚠️ Could not fetch poll state, status:', response.status);
          setError('Could not load poll information');
        }
      } catch (error) {
        console.error('❌ Error checking poll state:', error);
        setError('Failed to load poll');
      } finally {
        setIsCheckingPollState(false);
      }
    };

    checkPollState();
    
    // Set up polling to check for poll state changes every 5 seconds
    const interval = setInterval(checkPollState, 5000);
    
    return () => clearInterval(interval);
  }, [urlRoomId]);

  // Join poll room when we have poll data and player info
  useEffect(() => {
    if (pollData && (pollData.state === 'open' || pollData.state === 'active') && player && urlRoomId && connected) {
      console.log('🚪 Joining poll room for', pollData.state, 'poll:', pollData.id);
      joinPollRoom(urlRoomId, player.nickname, player.id);
    }
  }, [pollData, player, urlRoomId, connected, joinPollRoom]);

  const handleVote = (optionId: string) => {
    if (!urlRoomId || !player?.id || !pollData) return;
    
    console.log('🗳️ Submitting vote for option:', optionId);
    submitVote(urlRoomId, player.id, optionId);
    
    // After voting, the poll should become active and show results
    // The socket events should handle the transition to PollRoom component
  };

  // Show loading state while checking poll state
  if (isCheckingPollState) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/lobby')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-sans font-semibold text-lg">📊 Poll Room</h1>
        </div>
        
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading poll information...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/lobby')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-sans font-semibold text-lg">📊 Poll Room</h1>
        </div>
        
        <Card className="mb-6">
          <CardHeader>
            <div className="text-center">
              <div className="h-16 w-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
              <CardTitle className="font-sans font-semibold text-xl">Error Loading Poll</CardTitle>
              <CardDescription className="font-mono">
                {error}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-4">
              <Button onClick={() => navigate('/lobby')} className="w-full">
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show open or active poll for voting
  if (pollData && (pollData.state === 'open' || pollData.state === 'active')) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/lobby')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-sans font-semibold text-lg">📊 Poll Room</h1>
        </div>
        
        <Card className="mb-6">
          <CardHeader>
            <div className="text-center">
              <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="font-sans font-semibold text-xl">{pollData.title}</CardTitle>
              <CardDescription className="font-mono">
                {pollData.question}
              </CardDescription>
              <Badge variant={pollData.state === 'active' ? 'default' : 'secondary'} className="mt-2">
                {pollData.state === 'active' ? 'Active - Vote Now!' : 'Open - Waiting to Start'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  {pollData.state === 'active' 
                    ? 'Select your choice below to cast your vote'
                    : 'Poll is open but not yet active. Please wait for the admin to start voting.'}
                </p>
              </div>

              {/* Poll Options for Voting */}
              <div className="space-y-3">
                {pollData.options.map((option) => (
                  <Card key={option.id} className={`relative overflow-hidden transition-colors ${
                    pollData.state === 'active' ? 'hover:bg-primary/5 cursor-pointer' : 'opacity-75 cursor-not-allowed'
                  }`}>
                    <CardContent className="p-4">
                      <Button
                        variant="ghost"
                        className="w-full h-auto p-0 text-left justify-start"
                        onClick={() => handleVote(option.id)}
                        disabled={pollData.state !== 'active'}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{option.emoji}</span>
                          <span className="font-mono text-base font-medium">{option.label}</span>
                        </div>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  Poll opened on {new Date(pollData.createdAt).toLocaleDateString()}
                </p>
                <Button onClick={() => navigate('/lobby')} variant="outline" className="w-full">
                  Back to Home
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // For closed polls, let PollRoom component handle the display with full results
  // PollRoom has the logic to fetch historical poll data and show results
  if (pollData && pollData.state === 'closed') {
    console.log('📊 PollLobby: Poll is closed, returning null to let PollRoom handle it');
    // Return null so PollApp will show PollRoom instead, which fetches and displays results
    return null;
  }

  if (!connected) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Connecting to poll server...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (currentPoll) {
    // Poll is active, this will be handled by PollApp component
    console.log('📊 PollLobby: currentPoll exists, returning null to let PollApp show PollRoom');
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/lobby')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="font-sans font-semibold text-lg">📊 Poll Room</h1>
      </div>
      
      <Card className="mb-6">
        <CardHeader>
          <div className="text-center">
            <div className="bg-purple-100 border-2 border-purple-300 rounded-lg p-3 inline-block">
              <div className="text-sm text-purple-600 font-medium">Room ID (share this with others):</div>
              <div className="flex items-center gap-2 justify-center">
                <div className="text-lg font-mono font-bold text-purple-800">{urlRoomId}</div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(urlRoomId || '');
                    // Could add a toast notification here
                  }}
                  className="h-8 w-8 p-0"
                >
                  📋
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Players List */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Players ({players.length})</h3>
              <div className="grid gap-2">
                {players.map((roomPlayer) => (
                  <div
                    key={roomPlayer.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-100"
                  >
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{roomPlayer.nickname}</span>
                      {roomPlayer.id === player?.id && (
                        <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs">
                          You
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Waiting Message */}
            <div className="border-t pt-4">
              <div className="text-center">
                <p className="text-gray-600">
                  Waiting for a poll to start...
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default PollLobby;
